import { Subject, Subscription } from 'rxjs';
import { APP_CONFIG } from '@app/modules/core/config/config';
import { IAppConfig } from '@app/store/models/config.interface';
import { CoreService } from '@app/modules/core/services/core.service';
import { SocialHelper } from '@app/store/models/social-helper.interface';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FunctionsService, UploadFileExtensions } from '@app/modules/core/services/functions.service';
import { QrAssetResponse, QrGeneratorNavigatorMEMORIES, SortDirections, UserLargeFileUploadModes, percentageCalculator } from 'shared-library';
import { AfterContentInit, Component, DoCheck, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

declare const confetti: any
declare var PhotoSwipe: any
declare const bootstrap: any
declare var PhotoSwipeUI_Default: any

@Component({
    selector: 'app-qrs-memories',
    templateUrl: './qrs-memories.component.html',
    styleUrls: ['./qrs-memories.component.scss', '../qrs-wrapper.component.scss']
})
export class QrsMemoriesComponent implements OnInit, OnDestroy, DoCheck, AfterContentInit {

    layout_columns = 2 // should be divisible by 12
    private previous_layout_columns: number;

    @Input() qr_id: string;
    @Input() qr_url: string;
    @Input() team_id: string;
    @Input() short_id: string;
    @Input() total_responses = 0;
    @Input() social_data: SocialHelper;
    @Input() data: QrGeneratorNavigatorMEMORIES;

    private subscriptions: Subscription[] = [];

    gallery: any;
    batchSize = 100;
    loading = false;
    modal_element: any;
    canvas_element: any;
    uploading_images = false
    images: Array<QrAssetResponse> = []
    last_response: Array<QrAssetResponse>
    galleryElements: Array<HTMLElement> = []
    @ViewChild('myFileInput') myFileInput: ElementRef;

    readonly uploadImg = "uploadImg"
    uploader: FormGroup<QueuedForm>
    percentage_obs: Subject<number> = new Subject<number>()

    constructor(
        private formBuilder: FormBuilder,
        public readonly coreService: CoreService,
        public readonly functionService: FunctionsService,
        @Inject(APP_CONFIG) public readonly config: IAppConfig
    ) {
        this.setUploader()
    }

    ngOnInit(): void {
        this.loadImages()
        this.calculateColumns(window.innerWidth);
        this.functionService.initializeTooltip()
    }

    ngOnDestroy(): void {
        this.subscriptions.map(s => s.unsubscribe())
    }

    ngAfterContentInit(): void {
        setTimeout(() => {
            this.openCanvas(this.uploadImg)
        }, 50)
    }

    ngDoCheck(): void {
        if (this.layout_columns !== this.previous_layout_columns) {
            this.previous_layout_columns = this.layout_columns;
            this.initPhotoSwipe()
        }
    }

    openCanvas(id: string) {
        this.canvas_element = new bootstrap.Offcanvas(document.getElementById(id), {
            keyboard: false
        })
        this.canvas_element.show()
    }

    openModal(id: string) {
        this.modal_element = new bootstrap.Modal(document.getElementById(id), {
            keyboard: false
        })
        this.modal_element.show()
    }

    removeImages(index: number) {
        (this.uploader.controls.deleted).push(this.formBuilder.control(this.uploader.controls.assets.controls[index].controls.url.value));
        (this.uploader.controls.assets).removeAt(index)
    }

    downloadMemory(image: QrAssetResponse) {

        // Create an anchor element and trigger the download
        const anchor = document.createElement('a');
        anchor.href = image.location.original.url;
        anchor.download = image.file_name;

        anchor.target = "_blank"
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)

    }

    private async loadImages() {
        if (this.last_response && (!this.last_response.length || this.images.length >= this.total_responses || this.batchSize > this.last_response.length)) return
        this.loading = true;
        const param = { limit: this.batchSize, sort: SortDirections.DESC, last_key: this.last_response?.length ? this.last_response[this.last_response.length - 1].filter_by : null }
        if (!param.last_key) delete param.last_key
        this.last_response = await this.coreService.getQRAssets(this.short_id, param)
        let length_before = this.images.length
        if (!param.last_key) this.images = []
        for (const image of this.last_response) {
            this.images.push(image)
            if (this.gallery?.items) {
                this.gallery.items.push({
                    el: this.createGalleryItem(image),
                    msrc: image.location.thumbnail.url,
                    src: image.location.original.url,
                    w: image.location.original.dimension.w,
                    h: image.location.original.dimension.h
                });
                this.gallery.invalidateCurrItems();
            }
        }
        this.loading = false
        if (length_before !== this.images.length) {
            this.initPhotoSwipe()
        }
    }

    private createGalleryItem(image: QrAssetResponse): HTMLElement {
        // Create figure element
        let figure = document.createElement('figure');
        figure.setAttribute('itemprop', 'associatedMedia');
        figure.setAttribute('itemscope', '');
        figure.setAttribute('data-pswp-uid', image._id);
        figure.setAttribute('itemtype', 'http://schema.org/ImageObject');

        // Create anchor element
        let anchor = document.createElement('a');
        anchor.setAttribute('href', image.location.original.url);
        anchor.setAttribute('itemprop', 'contentUrl');
        anchor.setAttribute('data-size', `${image.location.original.dimension.w}x${image.location.original.dimension.h}`);

        if (this.isVideoFile(image.location.thumbnail.url)) {
            //     // Create video element
            let video = document.createElement('video');
            video.setAttribute('width', '100%');
            video.setAttribute('height', '100%');
            video.controls = true;

            let sourceElement = document.createElement('source');
            sourceElement.src = image.location.thumbnail.url;
            sourceElement.type = 'video/mp4';

            video.innerHTML = 'Your browser does not support the video tag.';
            video.appendChild(sourceElement);

            // Append video to anchor
            anchor.appendChild(video);
        } else {
            // Create image element
            let img = document.createElement('img');
            img.setAttribute('src', image.location.thumbnail.url);
            img.setAttribute('itemprop', 'thumbnail');
            img.setAttribute('alt', image.caption || "Memory asset");

            // Append image to anchor
            anchor.appendChild(img);
        }

        // Append anchor and figcaption to figure
        figure.appendChild(anchor);

        if (image.caption) {
            // Create figcaption element
            let figcaption = document.createElement('figcaption');
            figcaption.setAttribute('itemprop', image.caption);
            figcaption.appendChild(document.createTextNode(image.caption));

            // Append figcaption to figure
            figure.appendChild(figcaption);
        }


        // Append figure to the document body (or any other parent element)
        return figure
    }

    isVideoFile(fileName: string): boolean {
        const videoExtensions = this.config.allowed_video_types;
        const extension = fileName.toLowerCase().slice((fileName.lastIndexOf(".") - 1 >>> 0) + 2);
        return videoExtensions.includes(extension);
    }

    private async initPhotoSwipe() {
        this.galleryElements = this.images.map(ii => this.createGalleryItem(ii))

        const photoswipeParseHash = () => {
            const params: any = {};
            const hash = window.location.hash.substring(1)

            if (hash.length < 5) {
                return params;
            }

            let vars = hash.split('&');
            for (let i = 0; i < vars.length; i++) {
                if (!vars[i]) {
                    continue;
                }
                let pair = vars[i].split('=');
                if (pair.length < 2) {
                    continue;
                }
                params[pair[0]] = pair[1];
            }

            if (params.gid) {
                params.gid = parseInt(params.gid, 10);
            }

            return params;
        };

        // Parse URL and open gallery if it contains #&pid=3&gid=1
        let hashData = photoswipeParseHash();
        if (hashData.pid && hashData.gid) {
            const _index = +hashData.pid - 1
            if (_index !== -1 && this.galleryElements.length > _index)
                await this.openPhotoSwipe(hashData.pid, this.galleryElements[_index], true, true);
        }
    }

    openGallery(image: QrAssetResponse, event: Event) {
        event.preventDefault();
        event.stopPropagation();
        const index = this.galleryElements.findIndex(el => el.getAttribute('data-pswp-uid') === image._id)
        if (index >= 0) {
            // open PhotoSwipe if valid index found
            this.openPhotoSwipe(index, this.galleryElements[index]);
        }
    }

    private async openPhotoSwipe(index: number, galleryElement: HTMLElement, disableAnimation = false, fromURL = false) {

        const parseThumbnailElements = async () => {

            interface ItemInterface {
                w: number;
                h: number;
                src: string;
                pid?: number;
                msrc?: string;
                title?: string;
                el?: HTMLElement;
            }

            const items: Array<ItemInterface> = []
            for (let i = 0; i < this.galleryElements.length; i++) {

                const figureEl = this.galleryElements[i]; // <figure> element
                const linkEl = figureEl.children[0]; // <a> element
                let size: Array<string> = linkEl.getAttribute('data-size') ? linkEl.getAttribute('data-size').split('x').map(n => n) : [];

                if (!size.length) {
                    const getMeta = async (url: string): Promise<string[]> => {
                        return new Promise((y) => {
                            var img = new Image();
                            img.addEventListener("load", function () {
                                linkEl.setAttribute('data-size', `${this.naturalWidth}x${this.naturalHeight}`)
                                y([`${this.naturalWidth}`, `${this.naturalHeight}`])
                            });
                            img.src = url;
                        })
                    }
                    size = await getMeta(linkEl.getAttribute("href"))
                }

                // create slide object
                const item: ItemInterface = {
                    src: linkEl.getAttribute('href'),
                    w: parseInt(size[0], 10),
                    h: parseInt(size[1], 10)
                };

                if (figureEl.children.length > 1) {
                    // <figcaption> content
                    item.title = figureEl.children[1].innerHTML;
                }

                if (linkEl.children.length > 0) {
                    // <img> thumbnail element, retrieving thumbnail url
                    item.msrc = linkEl.children[0].getAttribute('src');
                }
                item.el = figureEl; // save link to element for getThumbBoundsFn
                items.push(item);
            }
            return items;
        };

        let items = await parseThumbnailElements();
        let pswpElement = document.querySelectorAll('.pswp')[0];
        // define options (if needed)
        const options: any = {
            loop: false,
            shareButtons: [
                { id: 'facebook', label: 'Share on Facebook', url: 'https://www.facebook.com/sharer/sharer.php?u={{url}}' },
                { id: 'twitter', label: 'Tweet', url: 'https://twitter.com/intent/tweet?text={{text}}&url={{url}}' },
                { id: 'pinterest', label: 'Pin it', url: 'http://www.pinterest.com/pin/create/button/?url={{url}}&media={{image_url}}&description={{text}}' },
            ],
            // define gallery index (for URL)
            galleryUID: this.short_id,
            getNumItemsFn: () => this.total_responses,
            getThumbBoundsFn: (index: number) => {
                // See Options -> getThumbBoundsFn section of documentation for more info
                let thumbnail = items[index].el.getElementsByTagName('img')[0], // find thumbnail
                    pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
                    rect = thumbnail.getBoundingClientRect();
                return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
            },
        };

        if (this.data.downloadable) {
            options.shareButtons.push({ id: 'download', label: 'Download image', url: '{{raw_image_url}}', download: true })
        }

        // PhotoSwipe opened from URL
        if (fromURL) {
            if (options.galleryPIDs) {
                // parse real index when custom PIDs are used
                // http://photoswipe.com/documentation/faq.html#custom-pid-in-url
                for (let j = 0; j < items.length; j++) {
                    if (items[j].pid == index) {
                        options.index = j;
                        break;
                    }
                }
            } else {
                // in URL indexes start from 1
                options.index = index - 1;
            }
        } else {
            options.index = index;
        }

        // exit if index not found
        if (isNaN(options.index)) {
            return;
        }

        if (disableAnimation) {
            options.showAnimationDuration = 0;
        }

        // Pass data to PhotoSwipe and initialize it
        this.gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);

        this.gallery.next = () => {
            if (this.gallery.options.loop || this.gallery.getCurrentIndex() < this.gallery.options.getNumItemsFn() - 1) {
                {
                    if (this.gallery.getCurrentIndex() + this.batchSize / 2 > this.gallery.items.length) {
                        this.loadImages()
                    }
                }
                this.gallery.goTo(this.gallery.getCurrentIndex() + 1);
            }
        }

        this.gallery.prev = () => {
            if (this.gallery.options.loop || this.gallery.getCurrentIndex() > 0) {
                this.gallery.goTo(this.gallery.getCurrentIndex() - 1);
            }
        }
        this.gallery.init();
    }

    @HostListener('window:scroll', ['$event'])
    onScroll(event: Event): void {
        const windowHeight = 'innerHeight' in window ? window.innerHeight : document.documentElement.offsetHeight;
        const body = document.body;
        const html = document.documentElement;
        const docHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);

        const windowBottom = windowHeight + window.pageYOffset;

        if (windowBottom >= docHeight - 100 && !this.loading) {
            // Load the next batch of images when scrolled to the bottom
            this.loadImages();
        }

        {
            const element = document.getElementById("back-to-top");
            if (element) {
                if (window.pageYOffset > 500) {
                    element.classList.add("show");
                } else {
                    element.classList.remove("show");
                }
            }
        }

    }

    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        // Determine the number of columns based on the window width
        this.calculateColumns((event.target as Window).innerWidth)
    }

    private calculateColumns(windowWidth: number) {
        // Determine the number of columns based on the window width
        if (windowWidth < 600) {
            this.layout_columns = 2;
        } else if (windowWidth < 900) {
            this.layout_columns = 4;
        } else {
            this.layout_columns = 6;
        }
    }

    async fileChangeEvent(event: any) {
        const max_files_per_round = 500
        if (!event.target.files.length || this.uploading_images) return

        const files: FileList = event.target.files
        for (let index = 0; index < files.length; index++) {
            const file = files.item(index);
            const ext = file.name.split(".").pop()
            if (![...this.config.allowed_image_types, ...this.config.allowed_video_types].includes(ext.toLowerCase())) {
                this.functionService.showErrorMessage(`.${ext} is not a supported file format, please select a different media file`)
                return
            }

            const max_file_upload_size = 250
            if (file.size / 1024 / 1024 > max_file_upload_size) {
                this.functionService.showErrorMessage(`Media file too large, it should be less than ${max_file_upload_size} MB`)
                return
            }
        }

        if (this.uploader.controls.assets.length + files.length > max_files_per_round) {
            this.functionService.showErrorMessage(`Please select ${max_files_per_round - (this.uploader.controls.assets.length + files.length)} files or less.`)
            return
        }

        const uploaded_length = this.uploader.controls.assets.value.filter(({ url }) => url).length
        const getFileDimension = (file: File): Promise<{ h: number; w: number; }> => {
            return new Promise((resolve, reject) => {

                setTimeout(() => resolve({ h: 500, w: 500 }), 60000 * 2)

                if (file.type.startsWith('image')) {
                    var image = new Image();
                    image.onload = function () {
                        resolve({
                            h: image.naturalHeight,
                            w: image.naturalWidth,
                        })
                    };
                    // Read the image file as a data URL
                    var reader = new FileReader();
                    reader.onload = function (event) {
                        image.src = event.target.result as string;
                    };
                    reader.readAsDataURL(file);
                } else if (file.type.startsWith('video')) {
                    var video = document.createElement('video');
                    video.addEventListener('loadedmetadata', () => {
                        resolve({
                            h: video.videoHeight,
                            w: video.videoWidth,
                        })
                    });

                    // Read the video file as a data URL
                    var reader = new FileReader();
                    reader.onload = function (event) {
                        video.src = event.target.result as string;
                    };
                    reader.readAsDataURL(file);
                } else {
                    reject('Unsupported file type.')
                }
            })
        }

        let all_good = true
        this.uploading_images = true

        try {

            let p_timer: any;
            let percentage = 1
            const smartProgressCalculator = (small: number, big: number) => {
                clearInterval(p_timer)

                if (small >= big) {
                    let max_percentage = 100
                    this.percentage_obs.next(percentage)
                    p_timer = setInterval(() => {
                        if (percentage < max_percentage) this.percentage_obs.next(++percentage)
                        if (percentage >= 100) {
                            this.uploading_images = false
                            clearInterval(p_timer)
                            percentage = 1
                        }
                    }, 10);
                    return
                }

                if (!small) {
                    let max_percentage = percentageCalculator(small + 1, big)
                    percentage = 1
                    this.percentage_obs.next(percentage)
                    p_timer = setInterval(() => {
                        if (percentage === 98) return
                        if (percentage < max_percentage) this.percentage_obs.next(++percentage)
                        if (percentage >= 100) {
                            this.uploading_images = false
                            clearInterval(p_timer)
                            percentage = 1
                        }
                    }, 500)
                    return
                }

                let min_percentage = percentageCalculator(small - 1, big)
                if (percentage < min_percentage) percentage = min_percentage
                let max_percentage = percentageCalculator(small, big)

                this.percentage_obs.next(percentage)
                p_timer = setInterval(() => {
                    if (percentage < max_percentage) this.percentage_obs.next(++percentage)
                    if (percentage >= 100) {
                        this.uploading_images = false
                        clearInterval(p_timer)
                        percentage = 1
                    }
                }, 100)
            }

            {
                for (let index = 0; index < files.length; index++) {
                    const file = files.item(index);
                    this.uploader.controls.assets.insert(0, this.formBuilder.group({
                        url: [""],
                        name: [file.name],
                        file_index: [index],
                        size: [this.functionService.formatBytes(file.size)],
                        dimension: this.formBuilder.group({
                            h: [0],
                            w: [0],
                        }),
                    }))
                }

                const submitMemories = async (asset: QueuedAsset, url: string) => {
                    try {
                        await this.coreService.submitQRResponses(this.short_id, {
                            deleted: this.uploader.controls.deleted.value,
                            description: this.uploader.controls.description.value,
                            assets: [{
                                ...asset,
                                url
                            }]
                        })
                        this.total_responses += 1
                    } catch (error) {
                        this.functionService.logToConsole(error)
                    }
                }

                const looper = async () => {
                    const current_index = this.uploader.controls.assets.value.findIndex(({ url }) => !url)
                    if (current_index === -1) {
                        smartProgressCalculator(1, 1);
                        return
                    }

                    const uploader_image = this.uploader.controls.assets.controls[current_index].value
                    const file = files.item(uploader_image.file_index)
                    smartProgressCalculator(this.uploader.controls.assets.value.filter(({ url }) => !!url).length - uploaded_length, (this.uploader.controls.assets.value.length - uploaded_length));

                    const _result = await Promise.all([
                        getFileDimension(file),
                        this.functionService.uploadFile({ body: "", header: "", file }, file.name.split(".").pop().toLowerCase() as UploadFileExtensions, UserLargeFileUploadModes.QRASSETS, null, true, this.team_id, files.length)
                    ])
                    const url = _result[1]
                    this.uploader.controls.assets.controls[current_index].controls.dimension.setValue(_result[0])

                    await submitMemories(this.uploader.controls.assets.controls[current_index].value as QueuedAsset, url)
                    this.uploader.controls.assets.controls[current_index].controls.url.setValue(url)

                    if (this.uploader.controls.assets.controls.length > 1) {
                        //Push done to end of list
                        const updated_value = this.uploader.controls.assets.controls[current_index].value
                        this.uploader.controls.assets.controls.splice(current_index, 1)
                        this.uploader.controls.assets.push(this.formBuilder.group({
                            name: [updated_value.name],
                            url: [updated_value.url],
                            file_index: [updated_value.file_index],
                            size: [updated_value.size],
                            dimension: this.formBuilder.group({
                                h: [updated_value.dimension.h],
                                w: [updated_value.dimension.w],
                            }),
                        }));
                    }
                    await looper()
                }
                await looper()
            }

        } catch (error) {
            all_good = false
            this.functionService.logToConsole(error)
            this.uploading_images = false
            this.functionService.showErrorMessage("Network error, please contact support if error persists!", 10000)
        }
        if (this.myFileInput && all_good)
            this.myFileInput.nativeElement.value = '';
    }

    showGalleryItems() {
        if (this.uploader.controls.assets.value.find(({ url }) => url)) {
            this.last_response = null
            this.loadImages()
            this.setUploader()
            this.doConfetti()
            if (this.data.contact.enable && this.data.contact.email) {
                this.coreService.notifyQRResponses(this.short_id)
            }
        }
        this.canvas_element?.hide()

    }

    private setUploader() {
        this.uploader = this.formBuilder.group({
            processing: this.formBuilder.control(false),
            description: this.formBuilder.control("", [Validators.maxLength(60)]),
            deleted: this.formBuilder.array([].map(_ => this.formBuilder.control(_, [Validators.pattern(this.functionService.urlRegex())]))),
            assets: this.formBuilder.array([].map(asset => this.formBuilder.group({
                url: this.formBuilder.control(asset.url, [Validators.pattern(this.functionService.urlRegex())]),
                name: this.formBuilder.control(asset.name, [Validators.required]),
                size: this.formBuilder.control(asset.size, [Validators.required]),
                file_index: this.formBuilder.control(asset.file_index, [Validators.required]),
                dimension: this.formBuilder.group({
                    h: this.formBuilder.control(asset.dimension.h),
                    w: this.formBuilder.control(asset.dimension.w),
                }),
            }))),
        })
    }

    changeBorderColor(hoverName: any, color: string) {
        hoverName.style.borderColor = color;
    }

    private doConfetti() {
        var duration = 5 * 1000;
        var animationEnd = Date.now() + duration;
        var defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

        function randomInRange(min, max) {
            return Math.random() * (max - min) + min;
        }

        var interval = setInterval(function () {
            var timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                return clearInterval(interval);
            }

            var particleCount = 50 * (timeLeft / duration);
            // since particles fall down, start a bit higher than random
            if (confetti) {

                confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
                confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
            }
        }, 250);
    }

}

interface QueuedForm {
    processing: FormControl<boolean>;
    description: FormControl<string>;
    deleted: FormArray<FormControl<string>>;
    assets: FormArray<FormGroup<QueuedAssetsForm>>;
}

interface QueuedAsset {
    url: string;
    name: string;
    size: string;
    file_index: number;
    dimension: {
        h: number;
        w: number;
    };
}

interface QueuedAssetsForm {
    url: FormControl<string>;
    name: FormControl<string>;
    size: FormControl<string>;
    file_index: FormControl<number>;
    dimension: FormGroup<{
        h: FormControl<number>;
        w: FormControl<number>;
    }>;
}
